import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Lock, Save, User } from 'lucide-react'
import { useMyManager, useUpdateMyManager, useChangePassword } from '../lib/useManagerProfile'
import { RecorderDownloadSection } from '../components/RecorderDownloadSection'
import { RecorderTokensSection } from '../components/RecorderTokensSection'

export function SettingsPage() {
  const me = useMyManager()
  const updateMe = useUpdateMyManager()
  const changePw = useChangePassword()

  const [name, setName] = useState('')
  const [department, setDepartment] = useState('')
  const [profileMsg, setProfileMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  const [newPw, setNewPw] = useState('')
  const [newPwConfirm, setNewPwConfirm] = useState('')
  const [pwMsg, setPwMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)

  // Populate form once manager row is loaded.
  useEffect(() => {
    if (me.data) {
      setName(me.data.name ?? '')
      setDepartment(me.data.department ?? '')
    }
  }, [me.data])

  async function onSaveProfile(e: React.FormEvent) {
    e.preventDefault()
    setProfileMsg(null)
    try {
      await updateMe.mutateAsync({
        name: name.trim() || null,
        department: department.trim() || null
      })
      setProfileMsg({ kind: 'ok', text: '保存しました' })
    } catch (e) {
      setProfileMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) })
    }
  }

  async function onChangePassword(e: React.FormEvent) {
    e.preventDefault()
    setPwMsg(null)
    if (newPw.length < 8) {
      setPwMsg({ kind: 'err', text: 'パスワードは8文字以上にしてください' })
      return
    }
    if (newPw !== newPwConfirm) {
      setPwMsg({ kind: 'err', text: '確認用パスワードが一致しません' })
      return
    }
    try {
      await changePw.mutateAsync(newPw)
      setNewPw('')
      setNewPwConfirm('')
      setPwMsg({ kind: 'ok', text: 'パスワードを更新しました' })
    } catch (e) {
      setPwMsg({ kind: 'err', text: e instanceof Error ? e.message : String(e) })
    }
  }

  return (
    <main className="min-h-full bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3">
        <Link
          to="/"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          title="ダッシュボードへ戻る"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <h1 className="text-lg font-bold text-gray-800">設定</h1>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
        {/* Profile */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">プロフィール</h2>
          </div>

          {me.isLoading ? (
            <div className="text-sm text-gray-400">読み込み中…</div>
          ) : me.error ? (
            <div className="text-sm text-red-600">{String(me.error)}</div>
          ) : !me.data ? (
            <div className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              この auth ユーザーに対応する manager 行が見つかりません。
              管理者にお問い合わせください。
            </div>
          ) : (
            <form onSubmit={onSaveProfile} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">メールアドレス</label>
                <input
                  type="email"
                  value={me.data.email}
                  disabled
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  メールアドレスは Supabase Auth の識別子のため、ここからは変更できません。
                </p>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">氏名</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="山田 太郎"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">部署</label>
                <input
                  type="text"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="マネジメント部"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                />
              </div>

              {profileMsg && (
                <div
                  className={`text-sm rounded-lg px-3 py-2 ${
                    profileMsg.kind === 'ok'
                      ? 'text-emerald-700 bg-emerald-50'
                      : 'text-red-600 bg-red-50'
                  }`}
                >
                  {profileMsg.text}
                </div>
              )}

              <button
                type="submit"
                disabled={updateMe.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {updateMe.isPending ? '保存中…' : '保存'}
              </button>
            </form>
          )}
        </section>

        {/* Password */}
        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">パスワード変更</h2>
          </div>

          <form onSubmit={onChangePassword} className="space-y-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">新しいパスワード</label>
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                placeholder="8文字以上"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">新しいパスワード(確認)</label>
              <input
                type="password"
                value={newPwConfirm}
                onChange={(e) => setNewPwConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
              />
            </div>

            {pwMsg && (
              <div
                className={`text-sm rounded-lg px-3 py-2 ${
                  pwMsg.kind === 'ok'
                    ? 'text-emerald-700 bg-emerald-50'
                    : 'text-red-600 bg-red-50'
                }`}
              >
                {pwMsg.text}
              </div>
            )}

            <button
              type="submit"
              disabled={changePw.isPending || !newPw}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
            >
              <Lock className="w-4 h-4" />
              {changePw.isPending ? '更新中…' : 'パスワードを更新'}
            </button>
          </form>
        </section>

        <RecorderDownloadSection />

        {/* Recorder Helper tokens */}
        {me.data && <RecorderTokensSection managerId={me.data.id} />}
      </div>
    </main>
  )
}
